import { isDevMode } from '../auth/authConfig'

const API_BASE = import.meta.env.VITE_API_URL || '/api'

let getTokenFn: (() => Promise<string>) | null = null

export function setTokenProvider(fn: () => Promise<string>) {
  getTokenFn = fn
}

export async function getAuthHeadersForStream(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  if (!isDevMode && getTokenFn) {
    const token = await getTokenFn()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }
  return headers
}

async function getHeaders(): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  if (!isDevMode && getTokenFn) {
    const token = await getTokenFn()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  return headers
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error')
    throw new Error(`API Error ${response.status}: ${errorText}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (!text) return undefined as T

  return JSON.parse(text) as T
}

export async function apiGet<T>(url: string): Promise<T> {
  const headers = await getHeaders()
  const response = await fetch(`${API_BASE}${url}`, { headers })
  return handleResponse<T>(response)
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const headers = await getHeaders()
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  return handleResponse<T>(response)
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const headers = await getHeaders()
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'PUT',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  return handleResponse<T>(response)
}

export async function apiDelete<T = void>(url: string): Promise<T> {
  const headers = await getHeaders()
  const response = await fetch(`${API_BASE}${url}`, {
    method: 'DELETE',
    headers,
  })
  return handleResponse<T>(response)
}

export async function apiPostFile<T>(url: string, file: File, fieldName = 'file', extraFields?: Record<string, string | undefined>): Promise<T> {
  const formData = new FormData()
  formData.append(fieldName, file)
  if (extraFields) {
    for (const [key, value] of Object.entries(extraFields)) {
      if (value !== undefined) formData.append(key, value)
    }
  }

  const headers: HeadersInit = {}
  if (!isDevMode && getTokenFn) {
    const token = await getTokenFn()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await fetch(`${API_BASE}${url}`, {
    method: 'POST',
    headers,
    body: formData,
  })
  return handleResponse<T>(response)
}

export async function apiGetBlob(url: string): Promise<Blob> {
  const headers = await getHeaders()
  delete (headers as Record<string, string>)['Content-Type']
  const response = await fetch(`${API_BASE}${url}`, { headers })
  if (!response.ok) {
    throw new Error(`API Error ${response.status}`)
  }
  return response.blob()
}
